import { FC, ReactNode } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Stack } from "@mui/material";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIconBlock } from "@/dashboard-design-system/DashboardIconBlock/DashboardIconBlock";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Delete, Edit, Move } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

interface QuestionCardProps {
  icon?: IconElement;
  label: string;
  /** Type line under the label, e.g. "Single-select • Male, Female, Other"; the type part renders italic. */
  subtitle?: ReactNode;
  required?: boolean;
  sortableId?: string;
  showDisabledReorderHandle?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  deleteDisabledTooltip?: string;
  labelTooltip?: string;
  questionIndex?: number;
  questionScope?: "buyer" | "participant";
  /** Identifies a fixed card that carries no question id, so a test can target it. */
  questionKey?: string;
}

export const QuestionCard: FC<QuestionCardProps> = ({
  icon,
  label,
  subtitle,
  required = false,
  sortableId,
  showDisabledReorderHandle = false,
  onEdit,
  onDelete,
  deleteDisabledTooltip,
  labelTooltip,
  questionIndex,
  questionScope,
  questionKey,
}) => {
  const { t } = useTranslate();

  const draggable = Boolean(sortableId);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId ?? "",
    disabled: !draggable,
  });

  const style = draggable
    ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }
    : undefined;

  return (
    <Stack
      ref={draggable ? setNodeRef : undefined}
      style={style}
      direction="row"
      alignItems="center"
      spacing={1.5}
      data-test="v4-question-card"
      data-question-index={questionIndex}
      data-question-scope={questionScope}
      data-question-key={questionKey}
      tabIndex={questionIndex !== undefined ? 0 : undefined}
      sx={(theme) => ({
        backgroundColor: theme.palette.surface.neutral.supershy,
        borderRadius: 1,
        px: 2,
        py: 1,
      })}
    >
      {draggable && (
        <DashboardIconButton
          {...attributes}
          {...listeners}
          variant="text"
          size="small"
          vibe="neutral"
          data-test="v4-question-drag-handle"
          sx={{ cursor: isDragging ? "grabbing" : "grab", transform: "rotate(90deg)" }}
        >
          <Move />
        </DashboardIconButton>
      )}
      {!draggable && showDisabledReorderHandle && (
        <DashboardTooltip title={t("dashboard", "stepQuestionsV4.reorderDisabledTooltip")} placement="top">
          <span>
            <DashboardIconButton
              variant="text"
              size="small"
              vibe="neutral"
              disabled
              data-test="v4-question-drag-handle-disabled"
              sx={{ transform: "rotate(90deg)" }}
            >
              <Move />
            </DashboardIconButton>
          </span>
        </DashboardTooltip>
      )}

      {icon && (
        <DashboardIconBlock vibe="neutral" size="medium" sx={{ flexShrink: 0 }}>
          {icon}
        </DashboardIconBlock>
      )}

      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <DashboardTypography variant="body2" sx={{ color: "text.neutral.intense" }}>
            {label}
            {required && "*"}
          </DashboardTypography>
          {labelTooltip && (
            <DashboardInformationTooltip title={labelTooltip} placement="top" slotProps={{ icon: { size: "small" } }} />
          )}
        </Stack>
        {subtitle && (
          <DashboardTypography
            variant="caption"
            sx={{
              color: "text.neutral.supershy",
              fontStyle: "italic",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {subtitle}
          </DashboardTypography>
        )}
      </Stack>

      <Stack direction="row" spacing={0.5}>
        {onEdit && (
          <DashboardIconButton
            variant="text"
            size="small"
            vibe="neutral"
            onClick={onEdit}
            data-test="v4-edit-question-button"
          >
            <Edit />
          </DashboardIconButton>
        )}
        {(onDelete || deleteDisabledTooltip) &&
          (deleteDisabledTooltip ? (
            <DashboardTooltip title={deleteDisabledTooltip} placement="top">
              <span>
                <DashboardIconButton
                  variant="text"
                  size="small"
                  vibe="neutral"
                  disabled
                  data-test="v4-delete-question-button"
                >
                  <Delete />
                </DashboardIconButton>
              </span>
            </DashboardTooltip>
          ) : (
            <DashboardIconButton
              variant="text"
              size="small"
              vibe="neutral"
              onClick={onDelete}
              data-test="v4-delete-question-button"
            >
              <Delete />
            </DashboardIconButton>
          ))}
      </Stack>
    </Stack>
  );
};
